import { all, takeLatest, call, put, select } from 'redux-saga/effects';
import { ActionType } from 'deox';
import { navigate } from '@reach/router';
import jwtDecode from 'jwt-decode';
import { AxiosError } from 'axios';

// import { executeAfterToast } from '@/utils';
import env from '@/env';
import { ReloadType } from '@/types';
import { EntityInstance } from '@/services/service-directory/entity';
import { OrganisationInstance } from '@/services/membership/organisation';
import {
  fetchEntitiesActions,
  fetchEntityBySubdomainActions,
  loadMoreEntitiesActions,
  fetchEntitiesComparisonActions,
  loadMoreEntitiesComparisonActions,
  fetchEntityInvitationActions,
  createOrUpdateEntityActions,
  fetchEntityByIdActions,
  fetchEntityViewOnlyActions,
  fetchEntityDashboardActions,
  reloadDefaultEntityActions,
  deleteEntityActions,
  generateIndexesActions,
  fetchEntitiesDirectoryActions,
  fetchCountriesEntityActions,
  fetchFeaturedEntitiesActions,
  fetchRelatedEntitiesActions,
} from '@/redux/actions/service-directory/entity';
import { fetchApisInfoActions } from '@/redux/actions/service-directory/api-info';
import { fetchAppsInfoActions } from '@/redux/actions/service-directory/app-info';
import { RootState } from '@/redux/reducers';

export interface EntityForm {
  organisationId: string;
  entityType?: string;
  name: string;
  logo?: File;
  primaryColor: string;
  accentColor: string;
  description?: string;
  landingPageTitleLine1?: string;
  landingPageTitleLine2?: string;
  landingPageTitleLine3?: string;
  landingPageWelcomeHeaderText?: string;
  landingPageWelcomeBodyText?: string;
  landingPageAppIntroHeaderText?: string;
  landingPageAppIntroBodyText?: string;
  landingPageApiIntroHeaderText?: string;
  landingPageApiIntroBodyText?: string;
  landingPageApiIntroPendingText?: string;
  landingPageNewsIntroText?: string;
  showApiOverview?: boolean;
}

export function* fetchEntitiesSaga(action: ActionType<typeof fetchEntitiesActions.request>) {
  const {
    pageNum,
    pageSize,
    searchEntity,
    referer,
    filterValues,
    location,
    region,
    country,
    listOrders,
    isAdminAffect,
    tag,
    feature,
    captchaToken,
  } = action.payload;
  try {
    const { data, paging } = yield call(
      EntityInstance.fetchEntities,
      pageNum,
      pageSize,
      searchEntity,
      filterValues,
      location,
      region,
      country,
      listOrders,
      isAdminAffect,
      tag,
      feature,
      captchaToken
    );
    yield put(fetchEntitiesActions.success({ data, paging, keyword: searchEntity }));
    referer && navigate(`${referer}`);
  } catch (err) {
    yield put(fetchEntitiesActions.failure(err));
  }
}

export function* fetchFeaturedEntitiesSaga(action: ActionType<typeof fetchFeaturedEntitiesActions.request>) {
  const { recaptchaToken } = action.payload;
  try {
    const { data } = yield call(
      EntityInstance.fetchEntities,
      1,
      4,
      '',
      undefined,
      undefined,
      undefined,
      undefined,
      'updatedAt-desc',
      false,
      undefined,
      undefined,
      recaptchaToken
    );
    yield put(fetchFeaturedEntitiesActions.success({ data }));
  } catch (err) {
    yield put(fetchFeaturedEntitiesActions.failure(err));
  }
}

export function* fetchEntitiesDirectorySaga(action: ActionType<typeof fetchEntitiesDirectoryActions.request>) {
  const { pageNum, pageSize, searchEntity, dataSource } = action.payload;
  try {
    const { data, paging } = yield call(
      EntityInstance.fetchEntitiesDirectory,
      pageNum,
      pageSize,
      searchEntity,
      dataSource
    );
    yield put(fetchEntitiesDirectoryActions.success({ data, paging }));
  } catch (err) {
    const errorResponse = (err as AxiosError)?.response;
    const errors = {
      status: errorResponse?.status,
      message:
        errorResponse?.data?.message ||
        errorResponse?.data?.error_description ||
        errorResponse?.data?.errors?.[0].message ||
        err.message,
    };
    yield put(fetchEntitiesDirectoryActions.failure(errors));
  }
}

export function* loadMoreEntitiesSaga(action: ActionType<typeof loadMoreEntitiesActions.request>) {
  const {
    pageNum,
    pageSize,
    searchEntity,
    filterValues,
    location,
    region,
    country,
    listOrders,
    isAdminAffect,
    tag,
    feature,
    recaptchaToken,
  } = action.payload;
  try {
    const { data, paging } = yield call(
      EntityInstance.fetchEntities,
      pageNum,
      pageSize,
      searchEntity,
      filterValues,
      location,
      region,
      country,
      listOrders,
      isAdminAffect,
      tag,
      feature,
      recaptchaToken
    );
    yield put(loadMoreEntitiesActions.success({ data, paging }));
  } catch (err) {
    yield put(loadMoreEntitiesActions.failure(err));
  }
}

export function* fetchEntityBySubdomainSaga(action: ActionType<typeof fetchEntityBySubdomainActions.request>) {
  const { subdomain } = action.payload;
  try {
    const entity = yield call(EntityInstance.fetchEntityBySubdomain, subdomain);
    yield put(fetchEntityBySubdomainActions.success(entity));
  } catch (err) {
    yield put(fetchEntityBySubdomainActions.failure(err));
  }
}

export function* fetchEntityByIdSaga(action: ActionType<typeof fetchEntityByIdActions.request>) {
  try {
    const { entityId } = action.payload;
    const entity = yield call(EntityInstance.fetchEntityById, entityId);
    yield put(fetchEntityByIdActions.success(entity));
  } catch (err) {
    const errorResponse = (err as AxiosError)?.response;
    const errors = {
      status: errorResponse?.status,
      message:
        errorResponse?.data?.message ||
        errorResponse?.data?.error_description ||
        errorResponse?.data?.errors?.[0].message ||
        err.message,
    };
    yield put(fetchEntityByIdActions.failure(errors));
  }
}

export function* fetchEntityViewOnlySaga(action: ActionType<typeof fetchEntityViewOnlyActions.request>) {
  try {
    const { subdomain, entityId, isAdminAffect, recaptchaToken } = action.payload;
    const entity = entityId
      ? yield call(EntityInstance.fetchEntityById, entityId)
      : yield call(EntityInstance.fetchEntityBySubdomain, subdomain || '', isAdminAffect, recaptchaToken);
    yield put(fetchEntityViewOnlyActions.success(entity));
  } catch (err) {
    const errorResponse = (err as AxiosError)?.response;
    const errors = {
      status: errorResponse?.status,
      message:
        errorResponse?.data?.message ||
        errorResponse?.data?.error_description ||
        errorResponse?.data?.errors?.[0].message ||
        err.message,
    };
    yield put(fetchEntityViewOnlyActions.failure(errors));
  }
}

export function* fetchEntityDashboardSaga(action: ActionType<typeof fetchEntityDashboardActions.request>) {
  try {
    const { subdomain, entityId, recaptchaToken } = action.payload;
    const entity = entityId
      ? yield call(EntityInstance.fetchEntityById, entityId)
      : yield call(EntityInstance.fetchEntityBySubdomain, subdomain || '', undefined, recaptchaToken);
    yield put(fetchEntityDashboardActions.success(entity));
  } catch (err) {
    const errorResponse = (err as AxiosError)?.response;
    const errors = {
      status: errorResponse?.status,
      message:
        errorResponse?.data?.message ||
        errorResponse?.data?.error_description ||
        errorResponse?.data?.errors?.[0].message ||
        err.message,
    };
    yield put(fetchEntityDashboardActions.failure(errors));
  }
}

export function* fetchEntityInvitationSaga(action: ActionType<typeof fetchEntityInvitationActions.request>) {
  try {
    const { invitationCode } = action.payload;
    const { organisationId: entityId } = jwtDecode(invitationCode);
    const entity = yield call(EntityInstance.fetchEntityById, entityId);
    yield put(fetchEntityInvitationActions.success(entity));
  } catch (err) {
    yield put(fetchEntityInvitationActions.failure(err));
  }
}

export function* fetchEntitiesComparisonSaga(action: ActionType<typeof fetchEntitiesComparisonActions.request>) {
  const { pageNumber, pageSize, complianceGroups, entityName, countries, sort } = action.payload;
  try {
    const { data, paging } = yield call(
      EntityInstance.fetchEntitiesComparison,
      pageNumber,
      pageSize,
      complianceGroups,
      entityName,
      countries,
      sort
    );
    yield put(fetchEntitiesComparisonActions.success({ data, paging }));
  } catch (err) {
    yield put(fetchEntitiesComparisonActions.failure(err));
  }
}

export function* loadMoreEntitiesComparisonSaga(action: ActionType<typeof loadMoreEntitiesComparisonActions.request>) {
  const { pageNumber, pageSize, complianceGroups, entityName, countries, sort } = action.payload;
  try {
    const { data, paging } = yield call(
      EntityInstance.fetchEntitiesComparison,
      pageNumber,
      pageSize,
      complianceGroups,
      entityName,
      countries,
      sort
    );
    yield put(loadMoreEntitiesComparisonActions.success({ data, paging }));
  } catch (err) {
    yield put(loadMoreEntitiesComparisonActions.failure(err));
  }
}

export function* createOrUpdateEntitySaga(action: ActionType<typeof createOrUpdateEntityActions.request>) {
  try {
    const { entityId, entity, getDefaultApis, adding, cb } = action.payload;
    const { userInfo } = yield select((state: RootState) => state.membershipUsers);
    const authUser = yield select((state: RootState) => state.authUser.data);
    const organisation = yield select((state: RootState) => state.serviceDirectoryEntity.entityDashboard);
    const { id: organisationId, subdomain } = yield call(
      EntityInstance.createOrUpdateEntity,
      { ...entity, organisationId: entityId },
      adding
    );

    if (getDefaultApis) {
      yield call(EntityInstance.getDefaultApis, entityId);
    }

    const roles = !userInfo.memberships.length
      ? authUser?.roles
      : [
          ...authUser?.roles,
          userInfo.memberships.find(
            (entity: { organisationId: string; roleName: string }) => entity.organisationId === organisation?.id
          )?.roleName || '',
        ];
    const isAdmin = roles.includes(env.roles.directoryAdmin) || roles.includes(env.roles.superAdmin);

    try {
      isAdmin
        ? yield call(
            OrganisationInstance.adminUpdateOrganisation,
            organisationId,
            userInfo.userId,
            organisation?.shortName || entity.name
          )
        : yield call(
            OrganisationInstance.updateOrganisation,
            organisationId,
            userInfo.userId,
            organisation?.shortName || entity.name
          );
    } catch (error) {
      console.log(error);
    }

    yield put(createOrUpdateEntityActions.success());
    cb && cb(subdomain);
    // cb && executeAfterToast(cb);
  } catch (err) {
    yield put(createOrUpdateEntityActions.failure(err));
  }
}

export function* deleteEntitySaga(action: ActionType<typeof deleteEntityActions.request>) {
  const { entityId } = action.payload;
  try {
    yield call(EntityInstance.deleteEntity, entityId);
    yield put(deleteEntityActions.success(entityId));
  } catch (err) {
    yield put(deleteEntityActions.failure(err));
  }
}

export function* reloadDefaultEntitySaga(action: ActionType<typeof reloadDefaultEntityActions.request>) {
  try {
    const { organisationId, type } = action.payload;
    yield call(EntityInstance.reloadDefaultEntity, organisationId, type);
    if (type === ReloadType.api) {
      yield put(fetchApisInfoActions.request(organisationId));
    } else if (type === ReloadType.app) {
      yield put(fetchAppsInfoActions.request(organisationId));
    }
    yield put(reloadDefaultEntityActions.success());
  } catch (err) {
    yield put(reloadDefaultEntityActions.failure(err));
  }
}

export function* generateIndexesSaga() {
  try {
    yield call(EntityInstance.generateIndexes);
    yield put(generateIndexesActions.success());
  } catch (err) {
    yield put(generateIndexesActions.failure(err));
  }
}

export function* fetchCountriesEntitySaga() {
  try {
    const { data, paging, meta } = yield call(EntityInstance.fetchCountries);
    yield put(fetchCountriesEntityActions.success(data, paging, meta));
  } catch (err) {
    yield put(fetchCountriesEntityActions.failure(err));
  }
}

export function* fetchRelatedEntitySaga(action: ActionType<typeof fetchRelatedEntitiesActions.request>) {
  try {
    const { currentEntityId, total, country, region, entityType } = action.payload;
    const { data } = yield call(EntityInstance.fetchRelatedEntity, currentEntityId, total, country, region, entityType);
    yield put(fetchRelatedEntitiesActions.success({ data }));
  } catch (err) {
    yield put(fetchRelatedEntitiesActions.failure(err));
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchFeaturedEntitiesActions.request.type, fetchFeaturedEntitiesSaga),
    takeLatest(fetchEntitiesActions.request.type, fetchEntitiesSaga),
    takeLatest(fetchEntitiesDirectoryActions.request.type, fetchEntitiesDirectorySaga),
    takeLatest(loadMoreEntitiesActions.request.type, loadMoreEntitiesSaga),
    takeLatest(fetchEntityBySubdomainActions.request.type, fetchEntityBySubdomainSaga),
    takeLatest(fetchEntityByIdActions.request.type, fetchEntityByIdSaga),
    takeLatest(fetchEntityViewOnlyActions.request.type, fetchEntityViewOnlySaga),
    takeLatest(fetchEntityDashboardActions.request.type, fetchEntityDashboardSaga),
    takeLatest(fetchEntityInvitationActions.request.type, fetchEntityInvitationSaga),
    takeLatest(fetchEntitiesComparisonActions.request.type, fetchEntitiesComparisonSaga),
    takeLatest(loadMoreEntitiesComparisonActions.request.type, loadMoreEntitiesComparisonSaga),
    takeLatest(createOrUpdateEntityActions.request.type, createOrUpdateEntitySaga),
    takeLatest(deleteEntityActions.request.type, deleteEntitySaga),
    takeLatest(reloadDefaultEntityActions.request.type, reloadDefaultEntitySaga),
    takeLatest(generateIndexesActions.request.type, generateIndexesSaga),
    takeLatest(fetchCountriesEntityActions.request.type, fetchCountriesEntitySaga),
    takeLatest(fetchRelatedEntitiesActions.request.type, fetchRelatedEntitySaga),
  ]);
}
